import React, {
  ComponentPropsWithoutRef,
  ReactEventHandler,
  useCallback,
  useState,
  useMemo,
} from 'react';
import '@rbx/ui';
import { resolveStaticSrc } from '../utils/staticLinkUtils';
import { useMarkdown } from './MarkdownProvider';

import useMarkdownImageStyles from './MarkdownImage.styles';

type MarkdownImageProps = ComponentPropsWithoutRef<'img'> & {
  imgHeight?: number;
  imgWidth?: number;
};

export default function MarkdownImage({
  src,
  className,
  width,
  height,
  imgWidth,
  imgHeight,
  ...otherProps
}: MarkdownImageProps) {
  const { classes, widths } = useMarkdown();
  const size = useMemo(() => {
    if (imgWidth && imgHeight) {
      if (!width && !height) {
        return { width: imgWidth, height: imgHeight };
      }
      if (width && !height) {
        const n = Number(width);
        if (n) {
          return { width, height: Math.round((n / imgWidth) * imgHeight) };
        }
      }
      return { width, height, ratio: imgWidth / imgHeight };
    }
    return { width: width ?? widths?.image, height };
  }, [height, imgHeight, imgWidth, width, widths?.image]);
  const {
    classes: { image },

    cx,
  } = useMarkdownImageStyles({ ratio: size.ratio });
  const resolvedSrc = resolveStaticSrc(src);
  const webpSource = `${resolvedSrc}.webp`;
  const [useWebP, setUseWebp] = useState(
    process.env.connectsRemoteServer && !resolvedSrc?.endsWith('.svg'),
  );
  const handleImageRenderError = useCallback<ReactEventHandler<HTMLImageElement>>(
    (e) => {
      if (e && useWebP) {
        setUseWebp(false);
      }
    },
    [useWebP],
  );
  return (
    // TODO: CRK-374 - replace this with Image component from @rbx/ui
    <picture>
      {useWebP && <source srcSet={webpSource} data-testid='__webp_source' type='image/webp' />}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        width={size.width}
        height={size.height}
        {...otherProps}
        src={resolvedSrc}
        className={cx(classes?.image, image, className)}
        onError={handleImageRenderError}
        loading='lazy'
      />
    </picture>
  );
}
